import React, {forwardRef, useEffect, useImperativeHandle, useState} from 'react';
import Moment from 'moment/min/moment-with-locales.min.js';
import {extendMoment} from 'moment-range';
import {NotificationManager} from 'react-notifications';
import {Col, Row, Button} from "react-bootstrap";
import {doesAbsenceOverlap, isFullAbsence} from "../../utils";
import {useTranslation} from "react-i18next";

// React Components
import AvailabilityModule from '../AvailabilityModule/AvailabilityModule';

// CSS Files
import './CollapsibleSelect.css';

const moment = extendMoment(Moment);

const CollapsibleSelect = (props, ref) => {
   const [open, setOpen] = useState(false);
   const [weekAvailability, setWeekAvailability] = useState(props.week_availability);
   const [availabilities, setAvailabilities] = useState([]);
   const [limitedAvailability, setLimitedAvailability] = useState(!!props.week_availability.availability_info.limited_availability);
   const [absenceStartDate, setAbsenceStartDate] = useState(new Date(props.enrollment.absence_start_date));
   const [absenceEndDate, setAbsenceEndDate] = useState(new Date(props.enrollment.absence_end_date));
   const { t, i18n } = useTranslation();
   moment.locale(i18n.language.split('-')[0]);
   let weekStartDate = moment(weekAvailability.start_date).format(`${t("common.date_formatting.day")} [${t("common.of")}] MMMM`);
   let weekEndDate = moment(weekAvailability.end_date).format(`${t("common.date_formatting.day")} [${t("common.of")}] MMMM`);

   useEffect(() => {
      let availabilities = fillAvailabilities();
      setAvailabilities(availabilities);
   }, []);

   const fillAvailabilities = () => {
      let availabilities = [];
      let week = props.week_availability.availability_info.week_data;

      if (typeof week !== 'undefined') {
         for (let day in week) {
            availabilities.push({
               day: week[day].week_day,
               available_morning: week[day].available_morning,
               available_lunch: week[day].available_lunch,
               available_afternoon: week[day].available_afternoon,
               t1800: isAvailable(week[day], 18, 0),
               t1830: isAvailable(week[day], 18, 30),
               t1900: isAvailable(week[day], 19, 0),
               t1930: isAvailable(week[day], 19, 30),
               t2000: isAvailable(week[day], 20, 0),
               t2030: isAvailable(week[day], 20, 30),
               t2100: isAvailable(week[day], 21, 0),
               t2130: isAvailable(week[day], 21, 30),
               t2200: isAvailable(week[day], 22, 0),
               t2230: isAvailable(week[day], 22, 30),
               t2300: isAvailable(week[day], 23, 0),
            });
         }
         if (availabilities.length === 1 && !(props.enrollment.state === "absent" && doesAbsenceOverlap(absenceStartDate, absenceEndDate, new Date(props.week_availability.start_date), new Date(props.week_availability.end_date)) && absenceEndDate.getDay() !== 1)) {
            availabilities.push({
               day: "",
               available_morning: false,
               available_lunch: false,
               available_afternoon: false,
            });
         }
      } else {
         availabilities.push({
            day: "",
            available_morning: false,
            available_lunch: false,
            available_afternoon: false,
         });
         availabilities.push({
            day: "",
            available_morning: false,
            available_lunch: false,
            available_afternoon: false,
         });
      }

      return availabilities;
   }

   const getData = () => {
      return {
         availabilities: availabilities,
         limitedAvailability: limitedAvailability
      }
   }

   useImperativeHandle(ref, () => ({
      props,
      getData
   }));

   const isAvailable = (day, hour, minutes) => {
      if (typeof day.day_data !== 'undefined') {
         let day_data = day.day_data
         for (let day_info in day_data) {
            if (day_data[day_info].hour === hour && day_data[day_info].minutes === minutes) {
               return true;
            }
         }
         return false;
      } else {
         return false;
      }
   }

   const toggle = () => {
      setOpen(!open);
   }

   const addAvailability = () => {
      let days = availabilities.map(availability => availability.day);
      let findDuplicates = arr => arr.filter((item, index) => arr.indexOf(item) !== index);
      let duplicates = findDuplicates(days);

      if (duplicates.length === 0) {
         let canAddAvailability = availabilities.every(availability =>
            availability.t1800 || availability.t1830 || availability.t1900 || availability.t1930 ||
            availability.t2000 || availability.t2030 || availability.t2100 || availability.t2130 ||
            availability.t2200 || availability.t2230 || availability.t2300 || availability.available_afternoon ||
            availability.available_morning || availability.available_lunch);

         if (canAddAvailability) {
            setAvailabilities([...availabilities, {
               day: "",
               available_morning: false,
               available_lunch: false,
               available_afternoon: false,
            }]);

         } else {
            NotificationManager.error(t("availabilities_page.errors.at_least_one_date"), t("common.error"), 4000);
         }
      } else {
         NotificationManager.error(t("availabilities_page.errors.same_days_alt"), t("common.error"), 4000);
      }
   }

   const removeAvailability = (index) => {
      let newAvailabilities = availabilities.filter((_, i) => i !== index);
      setAvailabilities(newAvailabilities);
   }

   const editAvailability = (index, data) => {
      let newAvailabilities = availabilities.map((availability, i) => i === index ? data : availability);
      setAvailabilities(newAvailabilities);
   }

   const shouldDisableInput = (start_date) => {
      let today = moment().format('YYYY-MM-DD');
      let moment_start_date = moment(start_date);
      let remaining_days = moment_start_date.diff(today, 'days');

      return remaining_days < 2;
   }

   return (
      <Col xs={12} md={6} className={"pb-3"}>
         {props.enrollment.state === 'absent' && isFullAbsence(props.enrollment, new Date(weekAvailability.start_date), new Date(weekAvailability.end_date)) &&
            <div className={'absent-banner'}>
               <p>{t("availabilities_page.absent")}</p>
            </div>
         }

         <div className={(isFullAbsence(props.enrollment, new Date(weekAvailability.start_date), new Date(weekAvailability.end_date)) ? "absent " : "") + "card" + ((shouldDisableInput(weekAvailability.start_date)) ? " unavailable" : " available")}>
            <div className="card-header d-flex" onClick={(isFullAbsence(props.enrollment, new Date(weekAvailability.start_date), new Date(weekAvailability.end_date)) || shouldDisableInput(weekAvailability.start_date)) ? undefined : toggle}>
               <p className="week-text mr-auto">{t("availabilities_page.week", { week: weekAvailability.number })}</p>
               <span className="week-date text-muted">{t("availabilities_page.week_range", {weekStartDate: weekStartDate, weekEndDate: weekEndDate})}</span>
            </div>

            <div className={"collapse-m" + (open ? ' in' : '') + " "}>
               <div className="card-body">

                  {shouldDisableInput(weekAvailability.start_date) &&
                     <p className="uneditable-week-tooltip">{t("availabilities_page.past_or_current_week_note")}</p>
                  }

                  <p className="week-text-tooltip pb-3">{t("availabilities_page.availabilities_criteria")}</p>

                  {availabilities.map((availability, index) => {
                     return <AvailabilityModule
                        key={`week${weekAvailability.number}availability${index}`}
                        week={props.week_availability.number}
                        showTextTooltip={index < 2}
                        allowExtraOptions={index >= 2 || (props.enrollment.state === "absent" && doesAbsenceOverlap(absenceStartDate, absenceEndDate, new Date(weekAvailability.start_date), new Date(weekAvailability.end_date)) && absenceEndDate.getDay() !== 1 && index + 1 > 1)}
                        availabilityNumber={index + 1}
                        data={availability}
                        availabilityChanged={(data) => {
                           editAvailability(index, data);
                        }}
                        removeAvailability={() => {
                           removeAvailability(index);
                        }}
                        absenceStartDate={absenceStartDate}
                        absenceEndDate={absenceEndDate}
                        gameWeekStartDate={new Date(weekAvailability.start_date)}
                        gameWeekEndDate={new Date(weekAvailability.end_date)}
                        disableInputs={shouldDisableInput(weekAvailability.start_date)}
                        show={!(limitedAvailability)}/>
                  })}

                  {!shouldDisableInput(weekAvailability.start_date) && <div>
                     <input onChange={() => setLimitedAvailability(!limitedAvailability)} style={{marginBottom: 20}} type="checkbox" checked={limitedAvailability} name="limited_availability"
                            id="limited_availability"></input>
                     <label htmlFor={"limited_availability"} style={{display: "inline", fontSize: "0.9rem"}} className="week-text-tooltip pl-3">{t("availabilities_page.limited_availability")}</label>
                  </div>}

                  <Row>
                     <Col xs={12}>
                        {!(limitedAvailability || shouldDisableInput(weekAvailability.start_date)) &&
                           <Button onClick={addAvailability} className="w-100 mb-3" variant={"secondary"}>{t("availabilities_page.add_availability")}</Button>
                        }
                     </Col>
                  </Row>

               </div>
            </div>
         </div>
      </Col>

   )
}

export default forwardRef(CollapsibleSelect);